/**
 * Window shortcuts pressed inside an app's frame.
 *
 * A sandboxed app view is its own document, so a keydown there never reaches
 * the window's keymap: with the cursor in an app, ⌘K did nothing. The shim
 * posts the chord up and the host replays it on its own document, where
 * `useProjectKeymap` handles it like any other press.
 *
 * ONLY chords that open window chrome, and only ones the view did not claim.
 * An app that binds ⌘B for bold calls preventDefault and keeps it; one that
 * never heard of ⌘K lets it through. Editing chords (⌘D, ⌘W, arrows) stay in
 * the frame, since replaying them could split or close a pane from inside an
 * app that meant something else by them.
 */
export const FORWARDED_CODES: readonly string[] = [
  'KeyK',
  // ⌘. and ⌘⇧.: the World thread (RFC 0336), from inside an app too.
  'Period',
  // ⌘J the composer, ⌘B the sidebar, ⌘\ the canvas side, ⌘/ the legend:
  // every chrome toggle in useProjectKeymap, so none works only outside apps.
  'KeyJ',
  'KeyB',
  'Backslash',
  'Slash',
  'KeyT',
  'KeyP',
  'Comma',
  'Digit1',
  'Digit2',
  'Digit3',
  'Digit4',
  'Digit5',
  'Digit6',
  'Digit7',
  'Digit8',
  'Digit9',
]

/** The forwarded chords that take shift: ⌘⇧. is the World call. */
export const SHIFT_FORWARDED_CODES: readonly string[] = ['Period']

/**
 * Chords a rich-text field acts on by itself, with no handler to call
 * preventDefault: ⌘B bolds a contenteditable natively. From one of those the
 * shim leaves the chord alone, so forwarding never costs a view its bold.
 */
export const NATIVE_EDITING_CODES: readonly string[] = ['KeyB']

/**
 * The host's check on a forwarded chord. A frame can post anything, so this
 * re-applies the same allowlist rather than trusting the shim, and returns the
 * init for the replayed event, or null to drop it.
 */
export function forwardedKey(msg: unknown): KeyboardEventInit | null {
  if (!msg || typeof msg !== 'object') return null
  const m = msg as Record<string, unknown>
  if (typeof m.code !== 'string' || !FORWARDED_CODES.includes(m.code))
    return null
  const metaKey = m.metaKey === true
  const ctrlKey = m.ctrlKey === true
  if (!metaKey && !ctrlKey) return null
  if (m.altKey === true) return null
  const shiftKey = m.shiftKey === true
  if (shiftKey && !SHIFT_FORWARDED_CODES.includes(m.code)) return null
  return {
    code: m.code,
    key: typeof m.key === 'string' ? m.key : '',
    metaKey,
    ctrlKey,
    shiftKey,
    bubbles: true,
    cancelable: true,
  }
}
